import type { ComponentType, ReactNode, SVGProps } from 'react';
import { cn } from '../cn.js';

export interface NavItem {
  readonly key: string;
  readonly label: string;
  readonly href: string;
  readonly icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Count shown as a pill, e.g. approvals awaiting this user. */
  readonly badge?: number;
  /** Hidden entirely when false, so a user never sees a route they cannot use. */
  readonly visible?: boolean;
}

export interface NavGroup {
  readonly label: string;
  readonly items: readonly NavItem[];
}

export interface SidebarProps {
  groups: readonly NavGroup[];
  currentPath: string;
  /** Injected so the design system stays router-agnostic. */
  renderLink: (props: {
    href: string;
    className: string;
    'aria-current': 'page' | undefined;
    children: ReactNode;
  }) => ReactNode;
  productName?: string;
  productTagline?: string;
  buildSha?: string;
  buildTime?: string;
  timezoneLabel?: string;
  onNavigate?: () => void;
  /**
   * Icons only, for the consoles.
   *
   * The SQL console divides the viewport four ways and the sidebar is the
   * one region in it nobody is reading -- so it earns the right to give its
   * width back. Collapsed it keeps every item, every icon and the active
   * marker; what it drops is text that a tooltip and the accessible name
   * still carry.
   *
   * Desktop only. The mobile drawer is an overlay over the page rather than a
   * column beside it, so it costs no width and collapsing it would remove
   * labels to save nothing.
   */
  collapsed?: boolean;
}

function isActive(currentPath: string, href: string): boolean {
  if (href === '/') return currentPath === '/';
  return currentPath === href || currentPath.startsWith(`${href}/`);
}

export function Sidebar({
  groups,
  currentPath,
  renderLink,
  productName = 'CSA',
  productTagline = 'Support Console',
  buildSha,
  buildTime,
  timezoneLabel,
  onNavigate,
  collapsed = false,
}: SidebarProps) {
  return (
    // The click handler observes bubbling activations from the links inside so
    // the mobile drawer closes even when the active link is re-clicked and the
    // route does not change. It is not itself interactive, and keyboard users
    // are covered because Enter on an anchor dispatches a click -- hence the
    // two suppressions below.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="flex flex-col h-full bg-surface" onClick={onNavigate}>
      <div
        className={cn(
          'flex items-center border-b border-line',
          collapsed ? 'justify-center px-2 py-4' : 'gap-3 px-5 py-5',
        )}
      >
        <div className="shrink-0 w-8 h-8 rounded bg-brand flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
            className="w-4 h-4 text-on-brand"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
            />
          </svg>
        </div>
        {collapsed ? null : (
          <div className="min-w-0">
            <p className="font-semibold text-sm text-body truncate">{productName}</p>
            <p className="text-xs text-muted truncate">{productTagline}</p>
          </div>
        )}
      </div>

      <nav
        aria-label="Main"
        className={cn(
          'flex-1 overflow-y-auto py-4',
          collapsed ? 'px-2 space-y-3' : 'px-3 space-y-5',
        )}
      >
        {/*
          Emptied groups are dropped before the loop rather than inside it,
          so "is this the first group" means the first one actually on screen.
          Deciding that from the original index would put a divider above the
          top of the list for anybody whose first section is all hidden.
        */}
        {groups
          .map((group) => ({ group, items: group.items.filter((i) => i.visible !== false) }))
          // Hide a whole section when the user holds none of its permissions,
          // rather than showing links that will 403.
          .filter(({ items }) => items.length > 0)
          .map(({ group, items }, groupIndex) => {
            return (
              <div key={group.label}>
                {/*
                Collapsed, the heading becomes a rule and the text goes to
                screen readers only. Dropping it outright would merge four
                groups into one undifferentiated column of icons, and the
                grouping is how somebody finds Roles without reading every
                tooltip.
              */}
                {collapsed ? (
                  <>
                    <span className="sr-only">{group.label}</span>
                    {groupIndex > 0 ? (
                      <hr className="mx-2 mb-2 border-line" aria-hidden="true" />
                    ) : null}
                  </>
                ) : (
                  <p className="px-3 mb-1 text-[10px] font-semibold text-subtle uppercase tracking-wider">
                    {group.label}
                  </p>
                )}
                <div className="space-y-0.5">
                  {items.map((item) => {
                    const active = isActive(currentPath, item.href);
                    const Icon = item.icon;

                    return (
                      // The tooltip is the label when the label is not rendered.
                      // It sits on the wrapper rather than the link because
                      // `renderLink` takes a fixed set of props, and a title on
                      // the parent applies to the anchor inside it.
                      <span key={item.key} title={collapsed ? item.label : undefined}>
                        {renderLink({
                          href: item.href,
                          'aria-current': active ? 'page' : undefined,
                          className: cn(
                            'relative flex items-center rounded-lg text-sm font-medium transition-colors',
                            collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2',
                            active
                              ? 'bg-brand-subtle text-brand-text'
                              : 'text-muted hover:bg-raised hover:text-body',
                          ),
                          children: (
                            <>
                              <span
                                className={cn(
                                  'absolute left-0 w-0.5 h-6 rounded-r',
                                  active ? 'bg-brand' : 'bg-transparent',
                                )}
                                aria-hidden="true"
                              />
                              <span className="relative shrink-0">
                                <Icon className="w-5 h-5 shrink-0" aria-hidden="true" />
                                {/*
                                Collapsed, the count rides the icon. A badge
                                in the normal place would be the widest thing
                                in a 3.5rem column and would push the icon
                                off-centre -- and "there are approvals
                                waiting" is the one thing in this list worth
                                still being able to see at a glance.
                              */}
                                {collapsed && item.badge && item.badge > 0 ? (
                                  <span
                                    className="absolute -right-1.5 -top-1 rounded-full bg-brand text-on-brand text-[9px] font-bold px-1 tabular-nums"
                                    aria-hidden="true"
                                  >
                                    {item.badge > 9 ? '9+' : item.badge}
                                  </span>
                                ) : null}
                              </span>
                              <span className={cn('truncate flex-1', collapsed && 'sr-only')}>
                                {item.label}
                              </span>
                              {!collapsed && item.badge && item.badge > 0 ? (
                                <span className="shrink-0 rounded-full bg-brand text-on-brand text-[10px] font-bold px-1.5 py-0.5 tabular-nums">
                                  {item.badge > 99 ? '99+' : item.badge}
                                </span>
                              ) : null}
                              {/*
                              The count, for a screen reader, in both states.
                              The visible pill is the same number but it is
                              not announced as what it counts.
                            */}
                              {item.badge && item.badge > 0 ? (
                                <span className="sr-only">{`${item.badge} waiting`}</span>
                              ) : null}
                            </>
                          ),
                        })}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </nav>

      {/*
        The build stamp and the time zone are the first things to go: they are
        reference material somebody looks up once, and there is no honest way
        to show a commit sha in 3.5rem. They come back with the labels.
      */}
      <div className={cn('border-t border-line px-4 py-3', collapsed && 'hidden')}>
        {timezoneLabel ? (
          <p className="flex items-center gap-1 text-[11px] text-subtle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
              className="w-3 h-3 shrink-0"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            <span className="truncate">{timezoneLabel}</span>
          </p>
        ) : null}
        {buildSha ? (
          <p
            className="mt-1 text-[11px] text-subtle font-mono truncate"
            title={buildTime ? `Built ${buildTime}` : undefined}
          >
            {buildSha}
          </p>
        ) : null}
      </div>
    </div>
  );
}
